import { connectParityApi } from "@/api/connectApi";
import { driveOpenUrl, ensureSpecialFolder, grantDriveAccess, uploadToDrive } from "@/lib/drive";

/** Start a browser download of a Blob. */
export function downloadBlob(blob: Blob, fileName: string) {
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

export interface BoardDriveSave {
    /** Opens the file in Drive. */
    url: string;
    fileName: string;
    /** Set when the file was saved but couldn't be shared with the meeting's members. */
    warning?: string;
}

/**
 * The Teams/OneDrive model, as for recordings: the PNG goes into the SAVER's
 * Drive ("Recordings" special folder) and is shared with the meeting's
 * members as viewers (download allowed). Connect never keeps the bytes.
 */
export async function saveBoardToDrive(opts: {
    blob: Blob;
    fileName: string;
    meetingId: string;
    myEmail?: string | null;
    token: string | undefined;
}): Promise<BoardDriveSave> {
    const folder = await ensureSpecialFolder("recordings", opts.token);
    const file = new File([opts.blob], opts.fileName, { type: "image/png" });
    const item = await uploadToDrive(file, folder.id, opts.token).promise;
    const result: BoardDriveSave = { url: driveOpenUrl(item.id), fileName: item.name };
    try {
        const me = (opts.myEmail ?? "").trim().toLowerCase();
        const members = await connectParityApi.meetingChatMembers(opts.meetingId, opts.token);
        const emails = Array.from(new Set(
            members.map((m) => m.email.trim().toLowerCase()).filter((e) => e.includes("@") && e !== me)
        ));
        if (emails.length > 0) {
            await grantDriveAccess(item.id, {
                emails,
                role: "viewer",
                allowDownload: true,
                source: "connect-recording",
                sourceRef: opts.meetingId
            }, opts.token);
        }
    } catch (err) {
        result.warning = `Saved to your Drive, but it couldn't be shared with the meeting (${err instanceof Error ? err.message : "Drive refused"}). Share it from Drive.`;
    }
    return result;
}
